/**
 * Throwing away your own draft, and saying why that is not the same as being rejected (R41).
 *
 * Editing an approved survey opens a `draftVersion` (R4), and until this existed there was no way
 * out of that edit except through a reviewer: `POST /:id/reject` demands `PENDING_APPROVAL`, so
 * undoing a one-letter change meant asking somebody to review it for the sole purpose of refusing
 * it.
 *
 * **The two actions can sit on the same screen, so the words have to keep them apart.** `reject` is
 * the verdict of whoever reviewed: it carries a reason and stays in the history. This is the author
 * binning their own draft, and it writes no review stamp at all — a month later the row still has
 * to say "foi recusada" or "foi abandonada", and the only thing keeping those apart is that one of
 * them wrote nothing.
 *
 * Two things the dialog states before the button: what is lost — the edits of **this** draft — and
 * what is not touched, which is everything about the version on the air.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
`;

const dangerStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const keepsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const headlineStyle = css`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const lineStyle = css`
  margin: 0;
`;

export interface SurveyDiscardDraftDialogProps {
  /** The version being thrown away. */
  draftVersion: number;
  /** The version on the air, which this does not touch. */
  liveVersion: number;
  /** Whether the draft had already been sent for approval — then this also withdraws the request. */
  isPendingApproval: boolean;
  isWorking: boolean;
  onDiscard: () => void;
  onClose: () => void;
}

export const SurveyDiscardDraftDialog = ({
  draftVersion,
  liveVersion,
  isPendingApproval,
  isWorking,
  onDiscard,
  onClose,
}: SurveyDiscardDraftDialogProps): JSX.Element => (
  <UTModal
    open
    position='center'
    maxWidth='560px'
    hasButtonClose
    modalContentId='survey-discard-draft-dialog'
    closeButtonId='survey-discard-draft-dialog-close'
    title={`Descartar o rascunho da versão ${draftVersion}`}
    description='Jogar fora a própria edição. Não passa por revisor e não é uma recusa.'
    content={
      <div className={contentStyle}>
        <div className={dangerStyle} role='alert'>
          <p className={headlineStyle}>
            Some tudo o que foi editado na versão {draftVersion} — as perguntas, as opções e as
            traduções dela. Não há como desfazer.
          </p>
          {isPendingApproval ? (
            <p className={lineStyle}>
              Como ela já foi enviada para aprovação, descartar também{' '}
              <strong>retira o pedido de revisão</strong>. Ninguém precisa dar um veredito para você
              desistir.
            </p>
          ) : null}
        </div>

        <div className={keepsStyle}>
          <p className={headlineStyle}>A versão {liveVersion} não é tocada</p>
          <p className={lineStyle}>
            Ela continua no ar exatamente como está, com o mesmo questionário e o mesmo estado de
            aprovação. As respostas ficam, e quem estava fora da fila antes desta edição continua
            fora depois.
          </p>
          <p className={lineStyle}>
            <strong>Descartar não é rejeitar.</strong> Rejeitar é o veredito de quem revisou:
            escreve um motivo e fica no histórico. Aqui nenhum carimbo de revisão é escrito, porque
            revisão nenhuma aconteceu.
          </p>
        </div>
      </div>
    }
    customButtons={[
      { text: 'Cancelar', variant: 'secondary', disabled: isWorking, onClick: onClose },
      {
        text: 'Descartar rascunho',
        state: 'danger',
        loading: isWorking,
        disabled: isWorking,
        onClick: onDiscard,
      },
    ]}
    onClose={onClose}
  />
);
